import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Breadcrumb, Button, Layout, theme } from "antd";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useMe } from "@/modules/auth/queries/auth.query";
import { ORGANIZATION_QUERY_KEY } from "@/modules/organizations/queries/organizations.query";
import { organizationService } from "@/modules/organizations/services/organization.service";
import { ThemeToggle } from "@/shared/components/ThemeToggle";
import { NotificationCenter } from "./NotificationCenter";
import { SidebarMenu } from "./SidebarMenu";
import { UserDropdown } from "./UserDropdown";

const { Header, Content } = Layout;

const CHILDREN_PREFETCH_LIMIT = 10;

const BREADCRUMB_MAP: Record<string, string> = {
	dashboard: "Dashboard",
	organizations: "Mening tashkilotim",
	children: "Quyi tashkilotlar",
	devices: "Qurilmalar",
	"cross-access": "Boshqa tashkilotlar",
	users: "Foydalanuvchilar",
	"organization-types": "Tashkilot turlari",
	positions: "Lavozimlar",
	students: "O'quvchilar",
	guardians: "Ota-onalar",
	classes: "Sinflar",
	reports: "Hisobotlar",
	trash: "Axlat qutisi",
	attendance: "Davomat",
	employees: "Xodimlar",
	"nvr-cameras": "NVR va Kameralar",
	stream: "Live stream",
	"faceid-devices": "Face ID qurilmalar",
	"face-enrollment": "Yuz ro'yxatga olish",
	settings: "Sozlamalar",
	shifts: "Smenalar",
	"academic-years": "O'quv yillari",
	weekdays: "Ish kunlari",
	subjects: "Fanlar",
	"room-types": "Xona turlari",
	staff: "Xodimlar",
	educational: "Ta'lim",
	classrooms: "Xonalar",
	"teaching-assignments": "Dars yuklamalari",
	schedule: "Dars jadvali",
	workers: "Xodimlar",
	teachers: "O'qituvchilar",
	"academic-terms": "Choraklar",
	holidays: "Tatil kunlari",
};

function useBreadcrumbItems() {
	const location = useLocation();
	const segments = location.pathname.split("/").filter(Boolean);

	if (segments.length === 0) {
		return [];
	}

	return segments.map((segment, index) => {
		const path = `/${segments.slice(0, index + 1).join("/")}`;
		const label = BREADCRUMB_MAP[segment] || segment;
		const isLast = index === segments.length - 1;
		const isUuid = /^[0-9a-f]{8}-/.test(segment);

		if (isUuid) {
			return { title: "Tafsilot" };
		}

		if (isLast) {
			return { title: label };
		}

		return { title: <Link to={path}>{label}</Link> };
	});
}

export function MainLayout() {
	const [collapsed, setCollapsed] = useState(false);
	const location = useLocation();
	const queryClient = useQueryClient();
	const { token } = theme.useToken();
	const breadcrumbItems = useBreadcrumbItems();

	const { data: meUser } = useMe();
	const parentOrgId =
		meUser?.organizations?.find((o) => o.isPrimary)?.id ?? meUser?.organizations?.[0]?.id;

	useEffect(() => {
		if (!parentOrgId || location.pathname === "/organizations") {
			return;
		}
		queryClient.prefetchQuery({
			queryKey: [
				ORGANIZATION_QUERY_KEY,
				"children",
				parentOrgId,
				{ page: 1, limit: CHILDREN_PREFETCH_LIMIT },
			],
			queryFn: () =>
				organizationService.listChildren(parentOrgId, { page: 1, limit: CHILDREN_PREFETCH_LIMIT }),
		});
	}, [parentOrgId, location.pathname, queryClient]);

	const siderWidth = collapsed ? 80 : 264;
	const isStreamPage = location.pathname.includes("/nvr-cameras/stream/");

	return (
		<Layout className="min-h-screen">
			<SidebarMenu collapsed={collapsed} />
			<Layout
				style={{
					marginLeft: siderWidth,
					transition: "margin-left 0.2s",
				}}
			>
				<Header
					className="flex items-center justify-between"
					style={{
						position: "fixed",
						top: 0,
						right: 0,
						left: siderWidth,
						zIndex: 100,
						height: 64,
						transition: "left 0.2s",
						background: token.colorBgContainer,
						borderBottom: `1px solid ${token.colorBorderSecondary}`,
						padding: "0 16px",
					}}
				>
					<div className="flex items-center gap-3">
						<Button
							type="text"
							icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
							onClick={() => setCollapsed(!collapsed)}
						/>
						{breadcrumbItems.length > 0 && <Breadcrumb items={breadcrumbItems} />}
					</div>
					<div className="flex items-center gap-2">
						<NotificationCenter />
						<ThemeToggle />
						<UserDropdown />
					</div>
				</Header>
				{isStreamPage ? (
					<div style={{ marginTop: 64, height: "calc(100vh - 64px)", overflow: "auto" }}>
						<Outlet />
					</div>
				) : (
					<Content
						style={{
							margin: 16,
							marginTop: 64 + 16,
							padding: 24,
							minHeight: 280,
							background: token.colorBgContainer,
							borderRadius: token.borderRadiusLG,
						}}
					>
						<div key={location.pathname} className="page-transition">
							<Outlet />
						</div>
					</Content>
				)}
			</Layout>
		</Layout>
	);
}
